<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Select from 'dashboard/components-next/select/Select.vue';
import { resolvePeriodRange } from '../../../helpers/reportChartHelpers';

// Emits the resolved absolute range whenever the selection changes, so the
// parent can apply one period filter across every widget at runtime.
const emit = defineEmits(['change']);

const { t } = useI18n();

const TIME_RANGES = computed(() => [
  {
    value: 'last_7_days',
    label: t('WGPT_CUSTOM_REPORTS.TIME_RANGES.LAST_7_DAYS'),
  },
  {
    value: 'last_30_days',
    label: t('WGPT_CUSTOM_REPORTS.TIME_RANGES.LAST_30_DAYS'),
  },
  {
    value: 'last_90_days',
    label: t('WGPT_CUSTOM_REPORTS.TIME_RANGES.LAST_90_DAYS'),
  },
  {
    value: 'custom',
    label: t('WGPT_CUSTOM_REPORTS.TIME_RANGES.CUSTOM'),
  },
]);

// Format a Date as a `YYYY-MM-DD` string in the local timezone for <input type="date">.
function toDateInputValue(date) {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

const today = new Date();
const selectedTimeRange = ref('last_30_days');
const customSince = ref(
  toDateInputValue(new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000))
);
const customUntil = ref(toDateInputValue(today));

const isCustomRange = computed(() => selectedTimeRange.value === 'custom');

function currentRange() {
  if (isCustomRange.value && customSince.value && customUntil.value) {
    return {
      since: new Date(`${customSince.value}T00:00:00`).toISOString(),
      // End at the last millisecond so events anywhere on the "until" day are
      // included (the backend filters occurred_at <= until, inclusive).
      until: new Date(`${customUntil.value}T23:59:59.999`).toISOString(),
    };
  }
  return resolvePeriodRange({ timeRange: selectedTimeRange.value });
}

// A user-driven change carries isDefault: false so consumers can let an explicit
// dashboard selection override per-widget presets, while the mount-time default
// (isDefault: true) leaves them alone.
watch([selectedTimeRange, customSince, customUntil], () => {
  if (isCustomRange.value && (!customSince.value || !customUntil.value)) return;
  emit('change', { ...currentRange(), isDefault: false });
});

// Announce the initial range so a (re)mounted picker always stays in sync with
// the parent's applied filter.
onMounted(() => emit('change', { ...currentRange(), isDefault: true }));
</script>

<template>
  <div class="flex flex-wrap items-center gap-3">
    <Select v-model="selectedTimeRange" :options="TIME_RANGES" />
    <template v-if="isCustomRange">
      <input
        v-model="customSince"
        type="date"
        :max="customUntil"
        :aria-label="t('WGPT_CUSTOM_REPORTS.VIEWER.FROM')"
        class="w-auto max-w-[9.5rem] appearance-none rounded-lg border-0 outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-slate-6 focus:outline-n-blue-9 bg-n-surface-1 py-2 px-3 text-sm text-n-slate-12 no-margin"
      />
      <input
        v-model="customUntil"
        type="date"
        :min="customSince"
        :aria-label="t('WGPT_CUSTOM_REPORTS.VIEWER.TO')"
        class="w-auto max-w-[9.5rem] appearance-none rounded-lg border-0 outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-slate-6 focus:outline-n-blue-9 bg-n-surface-1 py-2 px-3 text-sm text-n-slate-12 no-margin"
      />
    </template>
  </div>
</template>
